/**
 * NexusCRM Storefront API client.
 *
 * All requests go to  <NEXUSCRM_URL>/api/public/*
 * Every request carries:
 *   X-Api-Key          — storefront-scoped tenant API key (env var)
 *   X-Session-Token    — guest cart session UUID (always injected)
 *   Authorization      — Bearer <customer-jwt> when logged in
 *
 * Response shape from NexusCRM: plain data objects (no outer { success, data } wrapper).
 */

import axios from 'axios';
import { getSessionToken } from '../utils/session';

const NEXUSCRM_URL = process.env.REACT_APP_NEXUSCRM_URL || 'http://localhost:5000';
const STOREFRONT_API_KEY = process.env.REACT_APP_STOREFRONT_API_KEY || '';

const CUSTOMER_TOKEN_KEY = 'storefront-customer-token';

// ── Token helpers ─────────────────────────────────────────────────────────────

export const getCustomerToken = () => localStorage.getItem(CUSTOMER_TOKEN_KEY);
export const setCustomerToken = (token) => localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
export const removeCustomerToken = () => localStorage.removeItem(CUSTOMER_TOKEN_KEY);

// ── Axios instance ────────────────────────────────────────────────────────────

const sf = axios.create({
  baseURL: `${NEXUSCRM_URL}/api/public`,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

sf.interceptors.request.use((config) => {
  config.headers['X-Api-Key'] = STOREFRONT_API_KEY;
  config.headers['X-Session-Token'] = getSessionToken();

  const token = getCustomerToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
});

sf.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      removeCustomerToken();
    }
    return Promise.reject(error);
  },
);

// ── Store Config ──────────────────────────────────────────────────────────────

export const storeAPI = {
  getConfig: async () => {
    const res = await sf.get('/store');
    return res.data;
  },
};

// ── Product field normaliser ──────────────────────────────────────────────────

const transformVariant = (v) => v ? {
  ...v,
  // Normalise camelCase API fields → snake_case that components also expect
  color_code: v.colorCode || v.color_code,
  color_name: v.colorName || v.color_name,
  stock_quantity: v.stockQuantity || v.stock_quantity || v.stock || 0,
  image_url: v.imageUrl || v.image_url,
} : v;

const transformProduct = (p) => p ? {
  ...p,
  // ProductCard uses image_url; API returns imageUrl
  image_url: p.imageUrl || p.image_url,
  image: p.imageUrl || p.image_url,
  // category_id compatibility
  category_id: p.category?.id || p.category_id,
  // Normalise nested variants if present
  variants: Array.isArray(p.variants) ? p.variants.map(transformVariant) : [],
} : p;

// ── Products & Catalogue ──────────────────────────────────────────────────────

export const sfProductsAPI = {
  getAll: async (params = {}) => {
    const res = await sf.get('/products', { params });
    // Response is { data: [...], meta: {...} }
    const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
    const products = raw.map(transformProduct);
    return { products, meta: res.data?.meta || null };
  },
  getAllFlat: async (params = {}) => {
    const res = await sf.get('/products', { params: { limit: 100, ...params } });
    const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
    return raw.map(transformProduct);
  },
  getBySlug: async (slug) => {
    const productRes = await sf.get(`/products/${slug}`);
    const product = transformProduct(productRes.data);
    // Slug endpoint already returns full variants including images[] via include.
    // Only fall back to the separate /variants call if slug had no variants.
    if (product.variants?.length > 0) return product;
    const variantsRes = await sf.get(`/products/${slug}/variants`).catch(() => ({ data: [] }));
    const variants = Array.isArray(variantsRes.data) ? variantsRes.data.map(transformVariant) : [];
    return { ...product, variants };
  },
  getVariants: async (slug) => {
    const res = await sf.get(`/products/${slug}/variants`);
    const raw = Array.isArray(res.data) ? res.data : [];
    return raw.map(transformVariant);
  },
};

export const sfCategoriesAPI = {
  getAll: async () => {
    const res = await sf.get('/categories');
    return Array.isArray(res.data) ? res.data : [];
  },
};

// ── Offer Bundles ─────────────────────────────────────────────────────────────

export const sfOffersAPI = {
  getAll: async () => {
    const res = await sf.get('/offers');
    return res.data?.offerCategories || [];
  },
};

// ── Customer Auth ─────────────────────────────────────────────────────────────

export const sfAuthAPI = {
  register: async ({ name, email, password, phone }) => {
    const res = await sf.post('/auth/register', { name, email, password, phone });
    return res.data; // { token, customer }
  },
  login: async ({ email, password }) => {
    const res = await sf.post('/auth/login', { email, password });
    return res.data; // { token, customer }
  },
  forgotPassword: async (email) => {
    const res = await sf.post('/auth/forgot-password', { email });
    return res.data;
  },
  resetPassword: async ({ token, newPassword }) => {
    // Backend controller reads `password` field (not `newPassword`)
    const res = await sf.post('/auth/reset-password', { token, password: newPassword });
    return res.data;
  },
};

// ── Customer Profile ──────────────────────────────────────────────────────────

export const sfCustomerAPI = {
  getProfile: async () => {
    const res = await sf.get('/customer/me');
    return res.data;
  },
  updateProfile: async (data) => {
    const res = await sf.put('/customer/me', data);
    return res.data;
  },
  changePassword: async ({ currentPassword, newPassword }) => {
    const res = await sf.put('/customer/me/password', { currentPassword, newPassword });
    return res.data;
  },
  getOrders: async () => {
    const res = await sf.get('/customer/orders');
    // Backend returns { data: [...], meta: {...} }
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.data?.data)) return res.data.data;
    return res.data?.orders || [];
  },
  getOrder: async (ref) => {
    const res = await sf.get(`/customer/orders/${ref}`);
    return res.data;
  },
  cancelOrder: async (ref) => {
    const res = await sf.post(`/customer/orders/${ref}/cancel`);
    return res.data;
  },
};

// ── Cart ──────────────────────────────────────────────────────────────────────

export const sfCartAPI = {
  get: async () => {
    const res = await sf.get('/cart');
    return res.data; // { items: [...] }
  },
  addItem: async ({ productId, variantId, quantity }) => {
    const res = await sf.post('/cart/items', { productId, variantId, quantity });
    return res.data;
  },
  updateItem: async (itemId, quantity) => {
    const res = await sf.put(`/cart/items/${itemId}`, { quantity });
    return res.data;
  },
  removeItem: async (itemId) => {
    const res = await sf.delete(`/cart/items/${itemId}`);
    return res.data;
  },
  clear: async () => {
    const res = await sf.delete('/cart');
    return res.data;
  },
  merge: async (sessionToken) => {
    // Called after login — merges guest session cart into customer cart
    const res = await sf.post('/cart/merge', { sessionToken });
    return res.data;
  },
};

// ── Wishlist ──────────────────────────────────────────────────────────────────

export const sfWishlistAPI = {
  get: async () => {
    const res = await sf.get('/wishlist');
    // Backend returns { wishlistId, items: [...], count }
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.data?.items)) return res.data.items;
    return [];
  },
  add: async (productId) => {
    const res = await sf.post('/wishlist', { productId });
    return res.data;
  },
  remove: async (productId) => {
    const res = await sf.delete(`/wishlist/${productId}`);
    return res.data;
  },
  moveToCart: async (productId) => {
    const res = await sf.post(`/wishlist/${productId}/move-to-cart`);
    return res.data;
  },
};

// ── Coupons ───────────────────────────────────────────────────────────────────

export const sfCouponsAPI = {
  validate: async ({ code, cartTotal, customerEmail }) => {
    const res = await sf.post('/coupons/validate', { code, cartTotal, customerEmail });
    return res.data;
  },
};

// ── Orders (guest checkout) ───────────────────────────────────────────────────

export const sfOrdersAPI = {
  create: async (orderData) => {
    // orderData: { customerName, customerEmail, customerPhone, shippingAddress, items[], couponCode?, notes? }
    const res = await sf.post('/orders', orderData);
    return res.data; // { order: { id, orderRef, ... } }
  },
  track: async (ref) => {
    const res = await sf.get(`/orders/${ref}`);
    return res.data;
  },
};

// ── Payments (Razorpay) ───────────────────────────────────────────────────────

export const sfPaymentsAPI = {
  razorpayInitiate: async ({ orderId, orderRef }) => {
    const res = await sf.post('/payments/razorpay/initiate', { orderId, orderRef });
    return res.data; // { razorpayOrderId, keyId, amount, amountInPaise, currency, orderRef, orderId }
  },
  razorpayVerify: async ({ razorpayOrderId, razorpayPaymentId, razorpaySignature, orderId }) => {
    const res = await sf.post('/payments/razorpay/verify', {
      razorpayOrderId,
      razorpayPaymentId,
      razorpaySignature,
      orderId,
    });
    return res.data; // { success: true, orderRef, status: 'PAID' }
  },
};

// ── Contact Form ──────────────────────────────────────────────────────────────

export const sfContactAPI = {
  submit: async ({ firstName, lastName, email, phone, message }) => {
    const res = await sf.post('/contact', { firstName, lastName, email, phone, message });
    return res.data;
  },
};

export default sf;
